import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../utils/axiosConfig';
import BadgeDisplay, { Badge } from './BadgeDisplay';

interface BadgeAchievementPopupProps {
  badgeNames: string[];
  onClose: () => void;
}

const BadgeAchievementPopup: React.FC<BadgeAchievementPopupProps> = ({ badgeNames, onClose }) => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/badges');
        if (response.data.success) {
          const allBadges = response.data.data.badges as Badge[];
          // Filter badges by the names we received
          const earnedBadges = allBadges.filter(badge => 
            badgeNames.includes(badge.name)
          );
          setBadges(earnedBadges);
        }
      } catch (error) {
        console.error('Error fetching badge details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (badgeNames.length > 0) {
      fetchBadges();
    }
  }, [badgeNames]);

  const currentBadge = badges[currentBadgeIndex];
  const hasMultipleBadges = badges.length > 1;

  const handleNext = () => {
    if (currentBadgeIndex < badges.length - 1) {
      setCurrentBadgeIndex(currentBadgeIndex + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentBadgeIndex > 0) {
      setCurrentBadgeIndex(currentBadgeIndex - 1);
    }
  };

  if (loading || badges.length === 0) {
    return null;
  }

  return createPortal(
    <div className="badge-achievement-popup-overlay" onClick={onClose}>
      <div className="badge-achievement-popup" onClick={(e) => e.stopPropagation()}>
        <div className="badge-achievement-popup__star">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div className="badge-achievement-popup__content">
          <div className="badge-achievement-popup__icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 4C7 3.44772 7.44772 3 8 3H16C16.5523 3 17 3.44772 17 4V5H19C19.5523 5 20 5.44772 20 6V8C20 8.55228 19.5523 9 19 9H18V19C18 20.1046 17.1046 21 16 21H8C6.89543 21 6 20.1046 6 19V9H5C4.44772 9 4 8.55228 4 8V6C4 5.44772 4.44772 5 5 5H7V4Z" fill="#FFD700" stroke="#FFB300" strokeWidth="1.5"/>
              <path d="M8 9V19H16V9" fill="#FFED4E"/>
              <path d="M10 12H14" stroke="#FFB300" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M10 15H14" stroke="#FFB300" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="12" cy="7" r="1" fill="#FFB300"/>
            </svg>
          </div>
          
          <div className="badge-achievement-popup__badge-container">
            {currentBadge && (
              <div className="badge-achievement-popup__badge-wrapper">
                <BadgeDisplay
                  badge={currentBadge}
                  size="lg"
                  showDescription={true}
                  interactive={false}
                  className="badge-achievement-popup__badge"
                />
              </div>
            )}
          </div>
          
          <h2 className="badge-achievement-popup__title">Badge Unlocked!</h2>

          {hasMultipleBadges && (
            <div className="badge-achievement-popup__navigation">
              <button
                className="badge-achievement-popup__nav-button"
                onClick={handlePrevious}
                disabled={currentBadgeIndex === 0}
                aria-label="Previous badge"
              >
                ←
              </button>
              <span className="badge-achievement-popup__counter">
                {currentBadgeIndex + 1} / {badges.length}
              </span>
              <button
                className="badge-achievement-popup__nav-button"
                onClick={handleNext}
                disabled={currentBadgeIndex === badges.length - 1}
                aria-label="Next badge"
              >
                →
              </button>
            </div>
          )}

          <button 
            className="btn btn-primary badge-achievement-popup__button"
            onClick={hasMultipleBadges && currentBadgeIndex < badges.length - 1 ? handleNext : onClose}
          >
            {hasMultipleBadges && currentBadgeIndex < badges.length - 1 ? 'Next' : 'Continue'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BadgeAchievementPopup;

